import React, { useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Film } from 'lucide-react';
import { toast } from '../ui/Toast';
import { saveSTTHistory } from '../../services/speechApi';

const VoiceReelExtract = ({ onResult, onBack }) => {
  const [url, setUrl] = useState('');
  const [processing, setProcessing] = useState(false);
  const [action, setAction] = useState('transcribe');

  // Validation souple (alignée avec le backend /api/voice/reel) :
  // Reel Facebook, fb.watch, Instagram Reel, TikTok.
  const isValidUrl = /^https?:\/\//i.test(url) && (
    url.includes('facebook.com') || url.includes('fb.watch') ||
    url.includes('instagram.com') || url.includes('tiktok.com')
  );

  const handleExtract = async () => {
    if (!isValidUrl) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const reelRes = await axios.post('/api/voice/reel', { url, action }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 185000, // > timeout H1→H2 (180s) : laisse H1 renvoyer sa propre erreur en premier
      });

      const text = reelRes.data.text;
      const duration = reelRes.data.duration;

      // Historique Studio (même table speech_generations) — fire-and-forget
      saveSTTHistory(text, url, 0, 'fr', {
        source: 'reel',
        sourceUrl: url,
        provider: 'local-h2',
        model: 'whisper-large-v3',
        duration,
      }).catch(() => {});

      if (action === 'transcribe') {
        onResult(text, 'Transcription Reel');
      } else {
        const endpoint = action === 'summarize' ? '/api/voice/summarize' : '/api/voice/rewrite';
        const aiRes = await axios.post(endpoint, { text }, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          timeout: 60000,
        });
        onResult(aiRes.data.markdown, action === 'summarize' ? 'Résumé Reel' : 'Réécriture Reel');
      }
    } catch (err) {
      toast.error('Erreur: ' + (err.response?.data?.details || err.message));
    } finally {
      setProcessing(false);
    }
  };

  if (processing) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #475569', borderTopColor: '#f97316', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <div style={{ color: '#94a3b8', fontSize: 14 }}>Extraction en cours...</div>
        <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>
          Téléchargement + transcription, ça peut prendre 1 à 2 min
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={styles.backBtn}>
        <ArrowLeft size={14} /> Retour
      </button>

      {/* Action selector */}
      <div style={styles.actionRow}>
        {[['transcribe', 'Transcrire'], ['summarize', 'Résumer'], ['rewrite', 'Réécrire']].map(([key, label]) => (
          <button key={key} onClick={() => setAction(key)}
            style={{ ...styles.actionBtn, ...(action === key ? styles.actionActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {/* URL input */}
      <div style={styles.inputRow}>
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://facebook.com/reel/... ou tiktok.com/@.../video/..."
          style={styles.input}
        />
        <button onClick={handleExtract} disabled={!isValidUrl}
          style={{ ...styles.extractBtn, ...(isValidUrl ? {} : styles.extractBtnDisabled) }}>
          <Film size={14} /> Extraire
        </button>
      </div>

      <div style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>
        Collez un lien Reel Facebook, TikTok ou Instagram pour le transcrire
      </div>
    </div>
  );
};

const styles = {
  backBtn: {
    background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, marginBottom: 16, padding: 0,
  },
  actionRow: { display: 'flex', gap: 6, marginBottom: 16 },
  actionBtn: {
    flex: 1, padding: '8px 0', borderRadius: 6, border: '1px solid #475569',
    background: '#334155', color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 500,
  },
  actionActive: { background: '#f97316', color: 'white', borderColor: '#f97316' },
  inputRow: { display: 'flex', gap: 8 },
  input: {
    flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid #475569',
    background: '#0f172a', color: '#e2e8f0', fontSize: 13, outline: 'none',
  },
  extractBtn: {
    padding: '10px 16px', borderRadius: 8, border: 'none', background: '#f97316',
    color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
  },
  extractBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
};

export default VoiceReelExtract;
